import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';
import { useApp } from '../context/AppContext';
import { BrandLockup } from './BrandLockup';

interface LoginFormProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  email,
  onBack,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, clearError, hasErrors } = useFormValidation();
  const { dispatch } = useApp();
  const formRef = useRef<HTMLFormElement>(null);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      clearError('password');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !password.trim()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.login(email, password);
      
      // Check if user has admin role
      if (!authService.isAdmin(response.data.user)) {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Probe MFA with token only — do NOT set user/session UI until MFA passes.
      // Setting user first caused ProtectedRoute/HomePage to mount and spam 403s (FEAT-076).
      authService.setToken(response.data.token);

      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'post-fix',hypothesisId:'M1',location:'admin-web/src/components/LoginForm.tsx:preMfa',message:'Admin login succeeded; probing MFA before SET_USER',data:{role:response.data.user.role,hasToken:!!authService.getToken(),userSetYet:false},timestamp:Date.now()})}).catch(()=>{});
      // #endregion

      const mfaOk = await authService.validateAdminMfaAccess();
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'post-fix',hypothesisId:'M2',location:'admin-web/src/components/LoginForm.tsx:mfaResult',message:'Admin MFA probe result',data:{mfaOk},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (!mfaOk) {
        authService.removeToken();
        dispatch({ type: 'LOGOUT' });
        throw new Error(
          'Multi-factor authentication is required for admin access. Your password sign-in did not include an MFA second factor. Enroll MFA on this admin account in Firebase Auth, then use an MFA-capable sign-in — or ask an operator to temporarily set ADMIN_MFA_REQUIRED=false until MFA sign-in is available.',
        );
      }

      dispatch({
        type: 'SET_USER',
        payload: response.data.user,
      });
      dispatch({
        type: 'SET_ADMIN_ACCESS',
        payload: { isAdmin: true, checked: true },
      });
      
      onSuccess();
    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7550/ingest/04d8ad19-bf18-48f6-bd8a-8ab8dfd2699d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'c313e2'},body:JSON.stringify({sessionId:'c313e2',runId:'post-fix',hypothesisId:'M1',location:'admin-web/src/components/LoginForm.tsx:catch',message:'Admin login blocked or failed',data:{errorMessage:error instanceof Error ? error.message : String(error),hasToken:!!authService.getToken()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setApiError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(event);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        background: 'linear-gradient(135deg, #173B67 0%, #3478C8 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <BrandLockup height={48} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" color="primary">
              Admin Sign In
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Sign in to your admin account
        </Typography>

        <form 
          ref={formRef}
          onSubmit={handleFormSubmit} 
          method="post" 
          noValidate 
          autoComplete="on"
        >
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }} data-testid="admin-mfa-notice">
            Admin MFA enforcement is temporarily relaxed for bootstrap
            (`ADMIN_MFA_REQUIRED=false`). Re-enable after Firebase MFA sign-in ships.
          </Alert>

          <TextField
            fullWidth
            label="Admin Email Address"
            type="email"
            value={email}
            name="email"
            autoComplete="email"
            sx={{ mb: 3 }}
            helperText="Admin email address (cannot be changed)"
            InputProps={{
              readOnly: true,
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="password"
            autoComplete="current-password"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !password.trim()}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In to Admin'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Don't have admin access?{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onBack}
            >
              Go back to create one
            </Typography>
          </Typography>
        </form>
      </Paper>
    </Box>
  );
};
